import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Ticket, Radio, ChevronDown, ChevronUp, Mic2, Music, UtensilsCrossed, Store, Camera, Palette, Crown, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_ICONS = {
  headliner: Crown,
  direct_support: Mic2,
  opening_band: Music,
  food_vendor: UtensilsCrossed,
  merch_booth: Store,
  photographer: Camera,
  visual_artist: Palette,
};

const PUBLIC_SLOT_TYPES = ["opening_band", "direct_support", "food_vendor", "merch_booth", "photographer", "visual_artist"];

function StatusPill({ status }) {
  const map = {
    upcoming: ["#00F0FF", "Upcoming"],
    announced: ["#FF00E5", "Announced"],
    sold_out: ["#FDF8F5", "Sold Out"],
    past: ["#666", "Past"],
  };
  const [color, label] = map[status] || ["#00F0FF", status];
  return (
    <span className="font-mono-display text-[10px] uppercase tracking-[0.25em]" style={{ color }}>
      {label}
    </span>
  );
}

function ApplyModal({ show, defaultSlotType, onClose, labels }) {
  const [form, setForm] = useState({
    band_name: "",
    genre: "",
    contact_name: "",
    email: "",
    phone: "",
    social_links: "",
    epk_url: "",
    message: "",
    desired_slot_type: defaultSlotType || "opening_band",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.band_name || !form.contact_name || !form.email || !form.message) {
      return toast.error("Band/vendor name, contact, email, and message are required.");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/perform-application`, {
        ...form,
        show_id: show.id,
        phone: form.phone || null,
        genre: form.genre || null,
        social_links: form.social_links || null,
        epk_url: form.epk_url || null,
      });
      toast.success("Application sent. Stephen will respond personally.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="apply-modal" className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md overflow-y-auto p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-2xl w-full mx-auto my-10 glass-card p-7 md:p-10 border-[#FF00E5]/40"
      >
        <button
          data-testid="apply-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center border border-white/30 bg-black/60 text-white hover:border-[#FF00E5] hover:text-[#FF00E5] transition-all"
        >
          <X size={16} />
        </button>

        <span className="font-mono-display text-[10px] uppercase tracking-[0.35em] text-[#FF00E5]">Apply to perform</span>
        <h3 className="font-display font-black text-2xl md:text-3xl text-cream-soft mt-2 leading-tight">
          {show.venue}
        </h3>
        <p className="mt-1 font-mono-display text-[11px] uppercase tracking-[0.2em] text-white/55">
          {show.date_label} · {show.city}
        </p>

        <form onSubmit={submit} data-testid="apply-form" className="mt-6 space-y-5">
          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Applying for
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {PUBLIC_SLOT_TYPES.map((t) => {
                const Icon = SLOT_ICONS[t];
                const active = form.desired_slot_type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`apply-slot-${t}`}
                    onClick={() => setForm((f) => ({ ...f, desired_slot_type: t }))}
                    className={`flex items-center gap-2 px-3 py-2.5 border font-mono-display text-[10px] uppercase tracking-[0.2em] transition-all ${
                      active
                        ? "border-[#FF00E5] text-[#FF00E5] glow-magenta bg-[#FF00E5]/5"
                        : "border-white/15 text-white/55 hover:border-white/40"
                    }`}
                  >
                    <Icon size={12} /> {labels?.[t] || t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Band / Vendor Name <span className="text-[#FF00E5]">*</span>
              </label>
              <input data-testid="apply-band-name" required value={form.band_name} onChange={set("band_name")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" placeholder="Your name" />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Genre / Style
              </label>
              <input data-testid="apply-genre" value={form.genre} onChange={set("genre")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" placeholder="e.g. Post-hardcore" />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Contact Name <span className="text-[#FF00E5]">*</span>
              </label>
              <input data-testid="apply-contact-name" required value={form.contact_name} onChange={set("contact_name")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Email <span className="text-[#FF00E5]">*</span>
              </label>
              <input data-testid="apply-email" type="email" required value={form.email} onChange={set("email")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Phone (optional)
              </label>
              <input data-testid="apply-phone" value={form.phone} onChange={set("phone")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" />
            </div>
            <div>
              <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                EPK / Press Kit URL
              </label>
              <input data-testid="apply-epk" value={form.epk_url} onChange={set("epk_url")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" placeholder="https://..." />
            </div>
          </div>

          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Social Links (IG, Spotify, etc)
            </label>
            <input data-testid="apply-socials" value={form.social_links} onChange={set("social_links")} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" placeholder="Paste any relevant links" />
          </div>

          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Message <span className="text-[#FF00E5]">*</span>
            </label>
            <textarea
              data-testid="apply-message"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              className="w-full bg-black/40 border border-white/10 focus:border-[#FF00E5] outline-none px-3 py-2.5 text-cream-soft placeholder:text-white/30 resize-none text-sm"
              placeholder="Why you'd be a good fit for this show. Sample / demo links welcome."
            />
          </div>

          <button
            type="submit"
            data-testid="apply-submit"
            disabled={loading}
            className="btn-shine w-full md:w-auto px-8 py-3.5 bg-gradient-to-r from-[#FF00E5] via-[#a900b8] to-[#FF00E5] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 glow-magenta transition-all duration-500 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><Send size={13} /> Submit Application</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function ShowCard({ show, labels, onApply, isExpanded, onToggle }) {
  const openPublicSlots = (show.slots || []).filter(s => s.status === "open" && PUBLIC_SLOT_TYPES.includes(s.slot_type));
  const headlinerSlot = (show.slots || []).find(s => s.slot_type === "headliner");
  const headlinerName = headlinerSlot?.assigned_to || "Concept And Formality";

  return (
    <article
      data-testid={`show-card-${show.id}`}
      className="glass-card overflow-hidden reveal"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8 p-6 md:p-7">
        {/* Date */}
        <div className="md:w-44 flex md:flex-col items-center md:items-start gap-3 md:gap-1 border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:pr-6">
          <Radio size={14} className="text-[#FF00E5] animate-pulse md:hidden" />
          <div className="font-display font-black text-2xl md:text-3xl text-cream-soft leading-none">
            {show.date_label}
          </div>
          <StatusPill status={show.status} />
        </div>

        {/* Venue + headliner */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-white/55 text-xs font-mono-display uppercase tracking-[0.2em]">
            <MapPin size={12} /> {show.city}
          </div>
          <h3 className="font-display font-bold text-xl md:text-2xl text-cream-soft mt-1 leading-tight">
            {show.venue}
          </h3>
          <div className="mt-1.5 inline-flex items-center gap-1.5 font-mono-display text-[10px] uppercase tracking-[0.2em] text-white/60">
            <Crown size={11} className="text-[#FF00E5]" /> Headliner: <span className="text-cream-soft">{headlinerName}</span>
          </div>
        </div>

        {/* Slots + ticket */}
        <div className="flex flex-col items-stretch md:items-end gap-2">
          {openPublicSlots.length > 0 && (
            <button
              data-testid={`show-expand-${show.id}`}
              onClick={onToggle}
              className="px-4 py-2 border border-[#00F0FF]/60 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-mono-display text-[11px] uppercase tracking-[0.25em] hover:glow-cyan transition-all flex items-center gap-2"
            >
              {openPublicSlots.length} Slot{openPublicSlots.length !== 1 ? "s" : ""} Open
              {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          )}
          {show.ticket_url ? (
            <a
              data-testid={`show-ticket-${show.id}`}
              href={show.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 border border-[#FF00E5]/60 text-[#FF00E5] hover:bg-[#FF00E5]/10 font-mono-display text-[11px] uppercase tracking-[0.25em] hover:glow-magenta transition-all flex items-center gap-2 justify-center"
            >
              <Ticket size={13} /> Tickets
            </a>
          ) : (
            <span className="px-5 py-2.5 border border-white/15 text-white/40 font-mono-display text-[11px] uppercase tracking-[0.25em] flex items-center gap-2 justify-center">
              <Ticket size={13} /> Tickets · TBA
            </span>
          )}
        </div>
      </div>

      {/* Expanded slot list */}
      {isExpanded && openPublicSlots.length > 0 && (
        <div data-testid={`show-slots-${show.id}`} className="border-t border-white/10 bg-black/40 p-5 md:p-6">
          <p className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-4">
            // Open Slots · Tap to apply
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {openPublicSlots.map((slot) => {
              const Icon = SLOT_ICONS[slot.slot_type] || Music;
              return (
                <button
                  key={slot.id}
                  data-testid={`slot-apply-${slot.id}`}
                  onClick={() => onApply(show, slot.slot_type)}
                  className="group flex items-center gap-3 p-3.5 border border-[#00F0FF]/30 hover:border-[#FF00E5] hover:bg-[#FF00E5]/5 transition-all text-left"
                >
                  <span className="flex items-center justify-center w-9 h-9 bg-black/60 border border-[#00F0FF]/40 group-hover:border-[#FF00E5] transition-colors">
                    <Icon size={14} className="text-[#00F0FF] group-hover:text-[#FF00E5] transition-colors" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm text-cream-soft">
                      {labels?.[slot.slot_type] || slot.slot_type}
                    </div>
                    <div className="font-mono-display text-[9px] uppercase tracking-[0.25em] text-[#00F0FF] group-hover:text-[#FF00E5]">
                      Open · Apply →
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Shows() {
  const [shows, setShows] = useState([]);
  const [labels, setLabels] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null); // { show, defaultSlotType }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/shows`)
      .then((r) => {
        setShows(r.data.shows || []);
        setLabels(r.data.slot_type_labels || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalOpenSlots = shows.reduce((acc, s) => acc + (s.open_slot_count || 0), 0);

  return (
    <section id="shows" data-testid="shows-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16 gap-4 reveal">
        <div>
          <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#00F0FF]">
            03 — Live
          </span>
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 text-cream-soft">
            UPCOMING <span className="text-[#FF00E5] text-glow-magenta">SHOWS</span>
          </h2>
        </div>
        <div className="text-white/55 text-sm md:text-base font-mono-display uppercase tracking-[0.15em] max-w-sm md:text-right">
          {totalOpenSlots > 0 ? (
            <>
              <span className="text-[#00F0FF] text-glow-cyan">{totalOpenSlots} slot{totalOpenSlots !== 1 ? "s" : ""}</span> open across upcoming shows · Bands & vendors welcome
            </>
          ) : (
            <>Dates locking in · Mosh Team gets first crack at tickets</>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-white/55 font-mono-display text-xs uppercase tracking-[0.3em]">
          <Loader2 size={16} className="animate-spin mr-3" /> Loading shows…
        </div>
      ) : shows.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <p className="font-display font-bold text-xl text-cream-soft">No shows announced yet.</p>
          <p className="mt-2 text-white/55 text-sm font-mono-display uppercase tracking-[0.2em]">
            First dates land soon. Join Mosh Team for early notice.
          </p>
        </div>
      ) : (
        <div className="space-y-4 md:space-y-5">
          {shows.map((s) => (
            <ShowCard
              key={s.id}
              show={s}
              labels={labels}
              isExpanded={expandedId === s.id}
              onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
              onApply={(show, slotType) => setApplyTarget({ show, defaultSlotType: slotType })}
            />
          ))}
        </div>
      )}

      {applyTarget && (
        <ApplyModal
          show={applyTarget.show}
          defaultSlotType={applyTarget.defaultSlotType}
          labels={labels}
          onClose={() => setApplyTarget(null)}
        />
      )}
    </section>
  );
}
